// React
import { Fragment, useMemo, useState, useEffect } from "react"

// GraphQL
import { useUserOrganisationsQuery } from "@/api/graphql"

// Icons
import { CheckIcon, SelectorIcon } from "@/lib/icons"

// UI
import { cn } from "@/lib/utils"
import {
	Combobox,
	ComboboxInput,
	ComboboxButton,
	ComboboxOptions,
	ComboboxOption,
} from "@/lib/ui"

/**
 * OrganisationSelector
 * @param param0
 * @returns
 */
interface OrganisationSelectorProps {
	selectedOrganisationId?: string
	onChange: (id: string) => void
}
export function OrganisationSelector({
	selectedOrganisationId,
	onChange,
}: OrganisationSelectorProps) {
	const { data } = useUserOrganisationsQuery()

	// When data is loaded, set the selected organisation
	useEffect(() => {
		if (
			data?.me?.organisations?.length &&
			data?.me?.organisations?.[0]?.id
		) {
			onChange(data?.me?.organisations?.[0]?.id)
		}
	}, [data, onChange])

	const currentOrganisation = useMemo(() => {
		return data?.me?.organisations?.find(
			(org) => org?.id === selectedOrganisationId,
		)
	}, [data, selectedOrganisationId])

	// Search query
	const [query, setQuery] = useState<string>(
		currentOrganisation?.business_legal_name ?? "",
	)

	const sortedOrganisations = useMemo(() => {
		return (
			data?.me?.organisations?.sort(
				(a, b) =>
					a?.business_legal_name?.localeCompare(
						b?.business_legal_name ?? "",
					) ?? 0,
			) ?? []
		)
	}, [data?.me?.organisations])

	const filteredOrganisations =
		query === "" ||
		query === data?.me?.organisations?.[0]?.business_legal_name
			? sortedOrganisations
			: sortedOrganisations?.filter((organisation) => {
					return organisation?.business_legal_name
						?.toLowerCase()
						.includes(query.toLowerCase())
				})

	return (
		<Combobox
			as={Fragment}
			value={currentOrganisation?.id}
			onChange={(value) => {
				// Get organisation name
				const nextOrganisation = data?.me?.organisations?.find(
					(organisation) => organisation?.id === value,
				)?.business_legal_name

				if (nextOrganisation) {
					setQuery(nextOrganisation)
				}

				// Call onChange
				if (value) onChange(value)
			}}
		>
			<div className="relative mt-1 w-full max-w-xs self-end">
				<div className="relative w-full">
					<ComboboxButton className="w-full">
						<ComboboxInput
							className="w-full"
							onChange={(e) => {
								setQuery(e.currentTarget.value)
							}}
							displayValue={(organisationId: unknown) => {
								return (
									data?.me?.organisations?.find(
										(organisation) =>
											organisation?.id === organisationId,
									)?.business_legal_name ?? query
								)
							}}
						/>
						<div className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
							<SelectorIcon
								className="size-5 text-gray-400"
								aria-hidden="true"
							/>
						</div>
					</ComboboxButton>
				</div>

				{filteredOrganisations && filteredOrganisations?.length > 0 && (
					<ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
						{filteredOrganisations.map((organisation) => (
							<ComboboxOption
								key={organisation?.id}
								value={organisation?.id}
								className={({ active }) =>
									cn(
										"relative cursor-pointer py-2 pr-9 pl-3 select-none",
										active
											? "bg-primary-500"
											: "text-gray-900",
									)
								}
							>
								{({ selected }) => (
									<>
										<div className="flex">
											<span
												className={cn(
													"truncate",
													selected && "font-semibold",
												)}
											>
												{
													organisation?.business_legal_name
												}
											</span>
										</div>

										{selected && (
											<span className="text-black-500 absolute inset-y-0 right-0 flex items-center pr-4">
												<CheckIcon
													className="size-5"
													aria-hidden="true"
												/>
											</span>
										)}
									</>
								)}
							</ComboboxOption>
						))}
					</ComboboxOptions>
				)}
			</div>
		</Combobox>
	)
}
